import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { ASSETS } from "@/lib/assets";
import Testimonials from "@/components/Testimonials";
import { Sparkles, ScanLine, Hand, Clock, ArrowRight, Star } from "lucide-react";

const featureIcons = [Hand, ScanLine, Clock];

export default function Home() {
  const { t, user } = useApp();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/upload" : "/login");

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rise-in text-center lg:text-left flex flex-col items-center lg:items-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 px-4 py-1.5 text-xs uppercase tracking-widest text-amber-300 mb-6">
              <Sparkles size={13} /> {t.home.eyebrow}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] text-amber-50">
              {t.home.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed mx-auto lg:mx-0">{t.home.subtitle}</p>

            <div className="mt-9 action-row lg:!justify-start w-full sm:w-auto">
              <button
                onClick={go}
                data-testid="hero-readpalm-btn"
                className="btn-gold rounded-full px-8 py-3.5 text-base flex items-center gap-2 gold-glow"
              >
                <Sparkles size={18} /> {t.home.cta}
              </button>
              <button
                onClick={() => navigate("/instructions")}
                data-testid="hero-how-btn"
                className="btn-ghost-gold rounded-full px-6 py-3.5 text-base flex items-center gap-2"
              >
                {t.home.secondary} <ArrowRight size={16} />
              </button>
            </div>
            <p className="mt-6 text-xs text-amber-200/60 tracking-wide flex items-center justify-center lg:justify-start gap-2 text-center">
              <Star size={12} className="fill-amber-300 text-amber-300" /> {t.home.trust}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-purple-600/20 blur-3xl" />
            <div className="relative floaty rounded-[2rem] overflow-hidden border border-amber-500/25 gold-glow">
              <img src={ASSETS.hero} alt="Celestial palm" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 py-8">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-amber-100 text-center mb-12">
          {t.home.featuresTitle}
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {t.home.features.map((f, i) => {
            const Icon = featureIcons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="glass rounded-2xl p-7 hover:scale-[1.02] transition-transform"
                data-testid={`feature-card-${i}`}
              >
                <div className="h-12 w-12 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30 mb-5">
                  <Icon className="text-amber-300" size={22} />
                </div>
                <h3 className="font-serif text-xl text-purple-100 mb-2">{f.t}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <Testimonials />

      {/* Steps */}
      <section className="mx-auto max-w-7xl px-6 py-16">        <div className="glass-strong rounded-3xl p-8 sm:p-12">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-amber-100 text-center mb-10">
            {t.home.stepsTitle}
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {t.home.steps.map((s, i) => (
              <div key={i} className="text-center" data-testid={`step-${i}`}>
                <div className="mx-auto mb-4 h-14 w-14 rounded-full grid place-items-center font-serif text-2xl text-amber-300 border border-amber-500/40 bg-purple-900/40">
                  {i + 1}
                </div>
                <h3 className="font-serif text-lg text-amber-100 mb-1">{s.t}</h3>
                <p className="text-sm text-slate-400">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button onClick={go} data-testid="steps-cta-btn" className="btn-gold rounded-full px-8 py-3.5">
              {t.home.cta}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
